/**
 * The error catalog — every error the API can return (ADR-0010).
 *
 * Clients switch on `code`, never on text. `title` is a short, human sentence the web app may show;
 * `description` explains to developers when the error happens. `docs/04-engineering/error-catalog.md`
 * is generated from this object (`pnpm --filter @wednesday/contracts docs:errors`) and a test keeps
 * them identical.
 */

export const ERROR_AREAS = {
  general: 'General',
  auth: 'Authentication and accounts',
  projects: 'Projects and groups',
  work: 'Work items',
  collaboration: 'Collaboration and files',
  trash: 'Trash and notifications',
} as const;

export type ErrorArea = keyof typeof ERROR_AREAS;

export interface ErrorDefinition {
  readonly status: number;
  readonly area: ErrorArea;
  readonly title: string;
  readonly description: string;
}

export const errorCatalog = {
  // ── General ──────────────────────────────────────────────────────────────────────────────────
  VALIDATION_FAILED: {
    status: 400,
    area: 'general',
    title: 'Some fields are not valid.',
    description:
      'Request body, query or params do not match the contract (`errors[]` lists the fields).',
  },
  UNAUTHENTICATED: {
    status: 401,
    area: 'general',
    title: 'Please sign in to continue.',
    description: 'No valid session.',
  },
  FORBIDDEN: {
    status: 403,
    area: 'general',
    title: 'You do not have permission to do this.',
    description: 'Signed in but not allowed (generic).',
  },
  CLIENT_ID_REUSED: {
    status: 409,
    area: 'general',
    title: 'This was already sent with different content. Write it again.',
    description:
      'A clientId was sent again for a different task or a different update (ADR-0016): the phone app reused an ID.',
  },
  ORIGIN_MISMATCH: {
    status: 403,
    area: 'general',
    title: 'This request was blocked for your security.',
    description: 'State-changing request without a matching `Origin` header.',
  },
  DEMO_UNAVAILABLE: {
    status: 403,
    area: 'general',
    title: 'Not available in the demo.',
    description:
      'The public demo (D49) keeps no files and has no passwords, invitations or phones: uploads, password changes and resets, invitations and phone sign-in are refused there.',
  },
  DEMO_NOT_READY: {
    status: 503,
    area: 'general',
    title: 'The demo is starting. Try again in a minute.',
    description:
      'The public demo (D49) is loading its made-up data (the first start, or the nightly start again).',
  },
  NOT_FOUND: {
    status: 404,
    area: 'general',
    title: 'We could not find what you were looking for.',
    description: 'Generic missing resource.',
  },
  ROUTE_NOT_FOUND: {
    status: 404,
    area: 'general',
    title: 'This address does not exist.',
    description: 'No such endpoint.',
  },
  CONFLICT: {
    status: 409,
    area: 'general',
    title: 'This change conflicts with the current state.',
    description: 'Generic state conflict.',
  },
  PAYLOAD_TOO_LARGE: {
    status: 413,
    area: 'general',
    title: 'The request is too large.',
    description: 'Request body over the limit.',
  },
  UNSUPPORTED_MEDIA_TYPE: {
    status: 415,
    area: 'general',
    title: 'This type of content is not supported here.',
    description: 'Request `Content-Type` is not accepted by the endpoint.',
  },
  RATE_LIMITED: {
    status: 429,
    area: 'general',
    title: 'Too many requests. Please wait a moment and try again.',
    description: 'Too many requests (`Retry-After` header).',
  },
  INTERNAL: {
    status: 500,
    area: 'general',
    title: 'Something went wrong on our side.',
    description: 'Unexpected failure — look up the logs by `requestId`.',
  },
  SERVICE_UNAVAILABLE: {
    status: 503,
    area: 'general',
    title: 'The service is temporarily unavailable.',
    description: 'A dependency (database, Valkey) is down.',
  },

  // ── Authentication and accounts ─────────────────────────────────────────────────────────────
  AUTH_INVALID_CREDENTIALS: {
    status: 401,
    area: 'auth',
    title: 'Email or password is incorrect.',
    description: 'Wrong email or password (also an unknown or deactivated email).',
  },
  AUTH_ACCOUNT_LOCKED: {
    status: 423,
    area: 'auth',
    title: 'This account is temporarily locked after too many attempts.',
    description: 'Too many failed attempts; `details.retryAfterSeconds`.',
  },
  AUTH_PASSWORD_POLICY: {
    status: 422,
    area: 'auth',
    title: 'This password does not meet the requirements.',
    description: 'Password breaks a rule (`details.rule`: `length`, `equals_email`).',
  },
  AUTH_PASSWORD_BREACHED: {
    status: 422,
    area: 'auth',
    title: 'This password appeared in a data breach. Please choose another one.',
    description: 'Password appears in known breaches (Have I Been Pwned).',
  },
  AUTH_CURRENT_PASSWORD_WRONG: {
    status: 422,
    area: 'auth',
    title: 'Your current password is incorrect.',
    description: 'Change password: the current password is wrong.',
  },
  AUTH_RESET_TOKEN_INVALID: {
    status: 410,
    area: 'auth',
    title: 'This reset link has expired or was already used.',
    description: 'Reset link expired, used or unknown.',
  },
  INVITATION_INVALID: {
    status: 410,
    area: 'auth',
    title: 'This invitation is no longer valid.',
    description: 'Invitation expired, revoked, accepted or unknown.',
  },
  INVITATION_NOT_FOUND: {
    status: 404,
    area: 'auth',
    title: 'This invitation was not found.',
    description: 'No invitation with this ID.',
  },
  INVITATION_ALREADY_PENDING: {
    status: 409,
    area: 'auth',
    title: 'This person already has a pending invitation.',
    description: 'A pending invitation exists for this email.',
  },
  USER_EMAIL_TAKEN: {
    status: 409,
    area: 'auth',
    title: 'Someone already uses this email.',
    description: 'A user with this email exists.',
  },
  USER_NOT_FOUND: {
    status: 404,
    area: 'auth',
    title: 'This person was not found.',
    description: 'No such user.',
  },
  USER_LAST_ADMIN: {
    status: 409,
    area: 'auth',
    title: 'There must always be at least one active Admin.',
    description: 'Action would leave no active Admin.',
  },
  USER_SELF_DEACTIVATION: {
    status: 409,
    area: 'auth',
    title: 'You cannot deactivate your own account.',
    description: 'Admins cannot deactivate themselves.',
  },
  ADMIN_REQUIRED: {
    status: 403,
    area: 'auth',
    title: 'Only Admins can do this.',
    description: 'Endpoint requires the Admin role.',
  },
  VIEW_AS_PERSONAL: {
    status: 403,
    area: 'auth',
    title: 'Only they can change this. Go back to your own account to change yours.',
    description:
      'A change to the personal account (password, profile, notifications, signing in as someone) was attempted while signed in as another person (D45).',
  },
  TEAM_VIEW_FORBIDDEN: {
    status: 403,
    area: 'auth',
    title: 'My team is for Managers and Admins.',
    description: 'A Member asked for the stuck or overdue work of a team (D46, MOB-10).',
  },
  AUTH_AMBIGUOUS: {
    status: 400,
    area: 'auth',
    title: 'This request was signed in twice. Sign out and sign in again.',
    description: 'A request carried both a session cookie and a phone token (ADR-0014).',
  },
  PHONE_NOT_FOUND: {
    status: 404,
    area: 'auth',
    title: 'This phone is not connected any more.',
    description:
      'No connected phone with this ID for this person, or the request is not from a phone (ADR-0014).',
  },
  PHONE_NOT_ALLOWED: {
    status: 403,
    area: 'auth',
    title: 'Do this on your laptop.',
    description:
      'Administration and signing in as someone are not available in the phone app (D46, ADR-0014).',
  },
  VIEW_AS_SELF: {
    status: 409,
    area: 'auth',
    title: 'Pick someone other than yourself to sign in as.',
    description: 'An Admin or Manager asked to sign in as themselves (D41, D45).',
  },
  VIEW_AS_NOT_ALLOWED: {
    status: 403,
    area: 'auth',
    title: 'You can only sign in as people in your team.',
    description:
      'A Member, or a Manager for someone outside their team, an Admin or a demo person, asked to sign in as them (D45).',
  },
  TEAM_MANAGER_INVALID: {
    status: 409,
    area: 'auth',
    title: 'Only an active Manager has a team.',
    description: 'A team was set for someone who is not an active Manager, or a demo person (D45).',
  },
  TEAM_MEMBER_INVALID: {
    status: 422,
    area: 'auth',
    title: 'One of the selected people cannot join this team.',
    description:
      'Member unknown, inactive, an Admin, a demo person or the Manager themselves (D45).',
  },
  DEMO_PERSON_MANAGES_PROJECT: {
    status: 409,
    area: 'auth',
    title: 'A demo person still manages a real project. Give it another manager first.',
    description:
      'Removing the demo data would leave a project that is not demo data without a manager (D42); `detail` names the projects.',
  },

  // ── Projects and groups ─────────────────────────────────────────────────────────────────────
  PROJECT_NOT_FOUND: {
    status: 404,
    area: 'projects',
    title: 'This project was not found.',
    description: 'Missing or not visible to the actor.',
  },
  PROJECT_FORBIDDEN: {
    status: 403,
    area: 'projects',
    title: 'You cannot manage this project.',
    description: 'Visible but the actor cannot manage it.',
  },
  PROJECT_CREATE_FORBIDDEN: {
    status: 403,
    area: 'projects',
    title: 'Only Admins and Managers can create projects.',
    description: 'Members cannot create projects.',
  },
  PROJECT_ARCHIVED: {
    status: 409,
    area: 'projects',
    title: 'This project is archived. Unarchive it to make changes.',
    description: 'Write attempted on an archived project.',
  },
  PROJECT_MANAGER_INVALID: {
    status: 422,
    area: 'projects',
    title: 'The project manager must be an active Admin or Manager.',
    description: 'Manager is not an active Admin or Manager.',
  },
  PROJECT_MEMBER_INVALID: {
    status: 422,
    area: 'projects',
    title: 'One of the selected members cannot be added.',
    description: 'Member is not an active user, or the change removes the project manager.',
  },
  PROJECT_DATES_INVALID: {
    status: 422,
    area: 'projects',
    title: 'The end date must be on or after the start date.',
    description: 'End date before start date.',
  },
  GROUP_NOT_FOUND: {
    status: 404,
    area: 'projects',
    title: 'This group was not found.',
    description: 'Missing group.',
  },
  GROUP_LAST_IN_PROJECT: {
    status: 409,
    area: 'projects',
    title: 'A project needs at least one group.',
    description: 'Deleting the last group of a project.',
  },

  // ── Work items ──────────────────────────────────────────────────────────────────────────────
  TASK_NOT_FOUND: {
    status: 404,
    area: 'work',
    title: 'This task was not found.',
    description: 'Missing or not visible.',
  },
  TASK_FORBIDDEN: {
    status: 403,
    area: 'work',
    title: 'You cannot change this task.',
    description: 'Visible but the actor cannot make this change.',
  },
  TASK_CREATE_FORBIDDEN: {
    status: 403,
    area: 'work',
    title: 'Only project members can add tasks here.',
    description: 'Actor is not a member of the project.',
  },
  TASK_PARENT_INVALID: {
    status: 422,
    area: 'work',
    title: 'Subtasks can only be added to a top-level task in the same project.',
    description: 'Parent missing, is itself a subtask, or in another project.',
  },
  TASK_GROUP_INVALID: {
    status: 422,
    area: 'work',
    title: 'The selected group is not part of this project.',
    description: 'Group missing or in another project.',
  },
  TASK_OWNER_INVALID: {
    status: 422,
    area: 'work',
    title: 'One of the selected people cannot own this task.',
    description: 'Owner inactive or cannot see the project.',
  },
  TASK_TIMELINE_INVALID: {
    status: 422,
    area: 'work',
    title: 'The timeline needs a start and an end, and the end cannot be before the start.',
    description: 'Timeline end before start, or only one end set.',
  },
  LABEL_INVALID: {
    status: 422,
    area: 'work',
    title: 'This label cannot be used here.',
    description: 'Label missing, archived, or of the wrong kind.',
  },
  MOVE_TARGET_INVALID: {
    status: 422,
    area: 'work',
    title: 'This item cannot be moved there.',
    description: '`beforeId` / `afterId` are not siblings in the target.',
  },
  DESCRIPTION_VERSION_CONFLICT: {
    status: 409,
    area: 'work',
    title: 'Someone saved a newer version of this description.',
    description: 'Someone saved the description first; `details.currentVersion`.',
  },

  // ── Collaboration and files ─────────────────────────────────────────────────────────────────
  COMMENT_NOT_FOUND: {
    status: 404,
    area: 'collaboration',
    title: 'This update was not found.',
    description: 'Missing comment.',
  },
  COMMENT_FORBIDDEN: {
    status: 403,
    area: 'collaboration',
    title: 'You can only change your own updates.',
    description: "Editing / deleting someone else's comment.",
  },
  COMMENT_EMPTY: {
    status: 422,
    area: 'collaboration',
    title: 'Write something or attach a file first.',
    description: 'Comment has no text and no files.',
  },
  FILE_NOT_FOUND: {
    status: 404,
    area: 'collaboration',
    title: 'This file was not found.',
    description: 'Missing file.',
  },
  FILE_FORBIDDEN: {
    status: 403,
    area: 'collaboration',
    title: 'You cannot delete this file.',
    description: 'Deleting a file without permission.',
  },
  FILE_TOO_LARGE: {
    status: 413,
    area: 'collaboration',
    title: 'Files can be up to 50 MB.',
    description: 'File over 50 MB.',
  },
  FILE_EMPTY: {
    status: 422,
    area: 'collaboration',
    title: 'This file is empty.',
    description: 'Empty upload.',
  },

  // ── Trash and notifications ─────────────────────────────────────────────────────────────────
  TRASH_ENTRY_NOT_FOUND: {
    status: 404,
    area: 'trash',
    title: 'This item is no longer in the trash.',
    description: 'Missing, already restored or purged, or not visible.',
  },
  TRASH_CONTAINER_DELETED: {
    status: 409,
    area: 'trash',
    title: 'Restore the containing item first.',
    description: 'Restore blocked because the container is in Trash; `details.container`.',
  },
  NOTIFICATION_NOT_FOUND: {
    status: 404,
    area: 'trash',
    title: 'This notification was not found.',
    description: "Missing notification or not the recipient's.",
  },
  REMINDER_IN_PAST: {
    status: 422,
    area: 'trash',
    title: 'Pick a time that has not passed yet.',
    description: 'A reminder (NTF-6) was set for now or a moment before it.',
  },
  REMINDER_TOO_FAR: {
    status: 422,
    area: 'trash',
    title: 'Reminders can be set up to a year ahead.',
    description: 'A reminder (NTF-6) was set more than 366 days ahead.',
  },
} as const satisfies Record<string, ErrorDefinition>;

export type ErrorCode = keyof typeof errorCatalog;

export const ERROR_CODES = Object.keys(errorCatalog) as readonly ErrorCode[];

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && Object.hasOwn(errorCatalog, value);
}

/** Stable problem `type` URI for a code, e.g. `TASK_FORBIDDEN` → `…/errors/task-forbidden`. */
export function problemTypeFor(code: ErrorCode): string {
  return `https://tasklist.unhid.com/errors/${code.toLowerCase().replaceAll('_', '-')}`;
}
